import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import Button from "@/components/ui/button/Button";
import {
  ChevronLeft,
  Pencil,
  Coins,
  Globe,
  Clock,
  Settings,
  Calendar,
} from "lucide-react";
import { apiFetch } from "@/service/api";

interface ConfiguracionEmpresa {
  id: number;
  moneda: string;
  idioma: string;
  zona_horaria: string;
  logo: string;
  fecha_creacion: string;
}

export default function VerConfiguracion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState<ConfiguracionEmpresa | null>(null);
  const [loading, setLoading] = useState(true);
  const backend_api = import.meta.env.VITE_API_URL_BACKEND;

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await apiFetch(`configuracion-empresa/find-one/${id}`);
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
        }
      } catch (error) {
        console.error("Error fetching configuration:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-20">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
          Configuracion no encontrada
        </h3>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/multiempresa/configuracion_empresas")}
        >
          Volver al listado
        </Button>
      </div>
    );
  }

  const InfoCard = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string;
  }) => (
    <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-white/[0.02]">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-brand-500 shadow-sm dark:bg-gray-900">
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
          {value || "No especificado"}
        </p>
      </div>
    </div>
  );

  const getLogoUrl = (logoPath: string) => {
    if (!logoPath) return null;
    if (logoPath.startsWith("http")) return logoPath;
    const filename = logoPath.split("/").pop();
    return `${backend_api}/files/image/${filename}`;
  };

  return (
    <div>
      <PageBreadcrumb pageTitle="Detalle de Configuracion" />
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/multiempresa/configuracion_empresas")}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="size-5" />
          Volver al listado
        </button>
        <Button
          variant="primary"
          size="sm"
          startIcon={<Pencil className="size-4" />}
          onClick={() =>
            navigate(`/multiempresa/configuracion_empresas/update/${id}`)
          }
        >
          Editar Configuracion
        </Button>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-8">
        <div className="flex flex-col items-center justify-center border-b border-gray-100 pb-8 dark:border-gray-800 sm:flex-row sm:justify-start sm:gap-8">
          {config.logo ? (
            <img
              src={getLogoUrl(config.logo) || ""}
              alt="Logo"
              className="h-24 w-24 rounded-2xl object-contain border border-gray-100 dark:border-gray-800"
            />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500">
              <Settings className="size-12" />
            </div>
          )}
          <div className="mt-4 text-center sm:mt-0 sm:text-left">
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              ID de Configuracion: {config.id}
            </p>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard
            icon={<Coins className="size-5" />}
            label="Moneda"
            value={config.moneda}
          />
          <InfoCard
            icon={<Globe className="size-5" />}
            label="Idioma"
            value={config.idioma}
          />
          <InfoCard
            icon={<Clock className="size-5" />}
            label="Zona Horaria"
            value={config.zona_horaria}
          />
          <InfoCard
            icon={<Calendar className="size-5" />}
            label="Fecha Creacion"
            value={new Date(config.fecha_creacion).toLocaleDateString()}
          />
        </div>
      </div>
    </div>
  );
}
